import { useCallback, useEffect, useState } from "react";
import AirdropHeroSection from "./components/AirdropHeroSection";
import { getAllAirdop, getAllJoinedAirdop } from "../../api/apiAirdrop";
import { Card, message, Pagination, Skeleton } from "antd";
import { AirdropType } from "../../type";
import AirdropGridCard from "./components/AirdropGridCard";
import { AirdropFilterEnum } from "../../type/enum";
import { debounce } from "../../helpers";
import { apiLaunchPad } from "../../api/apiLaunchPad";
import { useAccount } from "wagmi";
import NoDataCard from "../../components/common/nodata-card";

const PER_PAGE = 10;

const Airdrop = () => {
  const [filter, setFilter] = useState<AirdropFilterEnum>(
    AirdropFilterEnum.AVAILABLE
  );
  const [airdrops, setAirdrops] = useState<AirdropType[]>([]);
  const [dashboardStatics, setDashboardStatic] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [searchValue, setSeacrhValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { address }: any = useAccount();
  const [initialFetch, setInitialFetch] = useState(true);

  const handleGetAirdrops = useCallback(
    async (
      filter: AirdropFilterEnum,
      searchValue: string,
      currentPage: number,
      firstRender = false
    ) => {
      try {
        setLoading(true);
        const response = await getAllAirdop(
          currentPage,
          PER_PAGE,
          filter,
          searchValue
        );
        setAirdrops(response.data);
        setTotalCount(response.total);
        //first time render
        if (response.data.length === 0 && firstRender)
          setFilter(AirdropFilterEnum.UPCOMING);
      } catch (error) {
        messageApi.open({
          type: "error",
          content: "An error occurred!",
        });
      } finally {
        setLoading(false);
      }
    },
    [messageApi]
  );
  const handleGetJoinedAirdrops = useCallback(
    async (searchValue: string, currentPage: number) => {
      try {
        setLoading(true);
        const response = await getAllJoinedAirdop(
          address,
          currentPage,
          PER_PAGE,
          searchValue
        );
        setAirdrops(response.data);
        setTotalCount(response.total);
      } catch (error) {
        messageApi.open({
          type: "error",
          content: "An error occurred!",
        });
      } finally {
        setLoading(false);
      }
    },
    [address, messageApi]
  );
  const handleGetDashboardStatic = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiLaunchPad.getDashboard();
      setDashboardStatic(response.data.data);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "An error occurred!",
      });
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  const debouncedSearchKey = debounce((value: string) => {
    setSeacrhValue(value);
  }, 1000);

  useEffect(() => {
    if (initialFetch) {
      setInitialFetch(false);
      handleGetAirdrops(filter, searchValue, currentPage, true);
    } else if (filter === AirdropFilterEnum.JOINED) {
      if (address) {
        handleGetJoinedAirdrops(searchValue, currentPage);
      } else {
        setFilter(AirdropFilterEnum.AVAILABLE);
      }
    } else {
      handleGetAirdrops(filter, searchValue, currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, searchValue, currentPage, address]);

  useEffect(() => {
    handleGetDashboardStatic();
  }, [handleGetDashboardStatic]);

  return (
    <>
      {contextHolder}
      <div className="bg-customBlue-100 pt-[87px]">
        <AirdropHeroSection staticData={dashboardStatics} />
        <div className="max-w-[1420px] mx-auto px-[5vw] lg:px-[10vw] 2xl:px-[10px] my-10">
          <div className="flex justify-between items-center gap-5 flex-col md:flex-row">
            <div className="self-start flex items-center min-[420px]:gap-1 sm:gap-2 font-Montserrat text-base font-semibold">
              {Object.entries(AirdropFilterEnum).map(([label, value]) => {
                if (!address && value === AirdropFilterEnum.JOINED)
                  return <></>;
                else
                  return (
                    <div
                      key={value}
                      className={`py-2 px-3 sm:py-3 sm:px-4 cursor-pointer text-xs min-[420px]:text-sm sm:text-base ${
                        filter === value
                          ? "bg-customBlue-500 text-white"
                          : "bg-transparent text-text-secondary"
                      }`}
                      onClick={() => setFilter(value as AirdropFilterEnum)}
                    >
                      {label}
                    </div>
                  );
              })}
            </div>
            <div className="w-full min-[400px]:w-fit self-end search-input">
              <input
                type="text"
                placeholder="Search by airdrop"
                className="f-search !w-full min-[400px]:!w-[230px] !pr-14"
                onChange={(event) => {
                  debouncedSearchKey(event.currentTarget.value);
                }}
              />
              <div className="icon">
                <img src="./images/icon-search.png" alt="" />
              </div>
            </div>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 min-[1100px]:grid-cols-3 2xl:grid-cols-4 gap-3 xl:gap-6">
            {!loading ? (
              airdrops.length === 0 ? (
                filter === AirdropFilterEnum.UPCOMING ? (
                  <img
                    src="/images/empty-project2.png"
                    alt=""
                    className="w-full h-auto object-contain max-h-[510px]"
                  />
                ) : (
                  <div className="col-span-12">
                    <NoDataCard />
                  </div>
                )
              ) : (
                <>
                  {airdrops.map((airdrop) => (
                    <AirdropGridCard key={airdrop.id} airdrop={airdrop} />
                  ))}
                </>
              )
            ) : (
              Array.from({ length: 5 }).map((_) => (
                <Card hoverable className="min-h-[400px] max-h-[510px]">
                  <Skeleton active />
                </Card>
              ))
            )}
          </div>
          {Number(totalCount) > 0 && airdrops?.length > 0 && (
            <Pagination
              current={currentPage}
              defaultCurrent={1}
              total={totalCount}
              pageSize={PER_PAGE}
              onChange={(page: number) => {
                setCurrentPage(page);
              }}
              showSizeChanger={false}
              className="pagiation-custom mt-6"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Airdrop;
